/**
 * @description 业务埋点核心层：队列、去重、计时、可视区曝光（参考 gcb-xcx tracker.js，适配 Nuxt 4 PC 端）
 */

import {
  buildProductTrackFields,
  resolveActivityTrackFields,
  resolveProductModelNo,
  resolveProductName,
  toPvEventName,
} from './tracking/helpers'
import { buildTrackSourceFields } from './tracking/sourceContext'
import { getTrackUserContext } from './tracking/userContext'

let _fm = null

/**
 * @description 注入 FrontendMonitor 实例（由 monitor.client.js 插件调用）
 * @param {Object} instance
 * @returns {void}
 */
export function setFM(instance) {
  _fm = instance
}

/**
 * @returns {Object|null}
 */
export function getFM() {
  return _fm
}

const _pageExposureSet = new Set()

export function hasExposedInPage(eventKey) {
  return _pageExposureSet.has(eventKey)
}

export function markExposedInPage(eventKey) {
  _pageExposureSet.add(eventKey)
}

export function clearPageExposure() {
  _pageExposureSet.clear()
}

/**
 * @description 可视曝光业务键对应的 data-* 选择器（观察节点或其子节点需具备其一）
 * @reason 无业务键时 IntersectionObserver 回调会被静默跳过，导致「只有点击/播放才像有曝光」
 */
const TRACK_BUSINESS_KEY_SELECTOR = [
  '[data-prod-id]',
  '[data-solution-id]',
  '[data-banner-id]',
  '[data-category-id]',
  '[data-activity-id]',
  '[data-case-id]',
  '[data-story-id]',
  '[data-story-pair]',
  '[data-story-track-type]',
  '[data-track-key]',
].join(', ')

/**
 * @description 从 dataset 解析曝光去重业务键
 * @param {DOMStringMap|Record<string, string>|null|undefined} ds
 * @returns {string|null}
 */
function getTrackBusinessKeyFromDataset(ds) {
  if (!ds) return null
  const storyId = ds.storyId ?? ds.storyid
  const storyTrackType = ds.storyTrackType ?? ds.storytracktype
  // 同一 story 下 solution/case 两张移动卡需分键，否则只会曝光其中一张
  if (storyId && storyTrackType) return `${String(storyId)}:${String(storyTrackType)}`
  const raw =
    ds.prodId ??
    ds.prodid ??
    ds.solutionId ??
    ds.solutionid ??
    ds.bannerId ??
    ds.bannerid ??
    ds.categoryId ??
    ds.categoryid ??
    ds.activityId ??
    ds.activityid ??
    ds.caseId ??
    ds.caseid ??
    storyId ??
    ds.trackKey ??
    ds.trackkey ??
    // storyPair 多为常量 "1"，仅作无 storyId 时的兜底
    ds.storyPair ??
    ds.storypair ??
    storyTrackType
  if (raw !== undefined && raw !== null && String(raw) !== '') return String(raw)
  if (ds.index !== undefined && ds.index !== null && String(ds.index) !== '') {
    return String(ds.index)
  }
  return null
}

/**
 * @description 从曝光节点 dataset 解析列表项
 * @param {Element|Object} el
 * @param {Array} list
 * @param {{ idKeys?: string[] }} [options]
 * @returns {Object|null}
 */
export function resolveTrackListItem(el, list, options = {}) {
  const rows = Array.isArray(list) ? list : []
  const idKeys = options.idKeys || [
    'prodId',
    'comboId',
    'solutionId',
    'activityId',
    'id',
    'categoryId',
  ]
  const ds = el?.dataset || {}
  const childDs = el?.querySelector?.(TRACK_BUSINESS_KEY_SELECTOR)?.dataset || {}
  const rawIds = [
    ds.prodId,
    ds.prodid,
    ds.solutionId,
    ds.solutionid,
    ds.bannerId,
    ds.bannerid,
    ds.categoryId,
    ds.categoryid,
    ds.activityId,
    ds.activityid,
    ds.caseId,
    ds.caseid,
    ds.storyId,
    ds.storyid,
    ds.trackKey,
    ds.id,
    childDs.prodId,
    childDs.prodid,
    childDs.solutionId,
    childDs.solutionid,
    childDs.bannerId,
    childDs.bannerid,
    childDs.categoryId,
    childDs.categoryid,
    childDs.activityId,
    childDs.activityid,
    childDs.caseId,
    childDs.caseid,
    childDs.storyId,
    childDs.storyid,
    childDs.trackKey,
    childDs.id,
  ].filter((v) => v !== undefined && v !== null && String(v) !== '')

  for (const raw of rawIds) {
    const found = rows.find((it) =>
      idKeys.some((k) => {
        const val = it?.[k]
        return val !== undefined && val !== null && String(val) === String(raw)
      })
    )
    if (found) return found
  }

  const index = Number(ds.index)
  if (Number.isFinite(index) && index >= 0 && index < rows.length) {
    return rows[index]
  }
  return null
}

const _TRACK_QUEUE = []
const _TRACK_DEDUP_MAP = new Map()
const MAX_QUEUE_SIZE = 100
const FLUSH_INTERVAL = 5000
const FLUSH_IMMEDIATE_SIZE = 20
const DEDUP_WINDOW_MS = 1000

let _flushTimer = null
let _isFlushing = false

function _getEventKey(eventName, targetType, targetId, eventType, properties = {}) {
  const base = `${eventName}|${eventType}|${targetType}|${targetId}`
  const depth = properties?.scroll_depth ?? properties?.depth_percent ?? properties?.depth_threshold
  // 滚动/播放深度：同一 target 下 25/50/75/100 必须分键，否则 1s 去重会把多档合并串掉
  if (depth !== undefined && depth !== null && String(depth) !== '') {
    return `${base}|sd:${depth}`
  }
  if (/Scroll_Depth|Play_Depth|scroll_depth/i.test(String(eventName || ''))) {
    return `${base}|sd:unknown`
  }
  return base
}

function _getSessionId() {
  if (!import.meta.client) return `sess_${Date.now()}`
  try {
    let sid = localStorage.getItem('_track_session_id')
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      localStorage.setItem('_track_session_id', sid)
    }
    return sid
  } catch {
    return `sess_${Date.now()}`
  }
}

function _getDeviceId() {
  if (!import.meta.client) return `dev_${Date.now()}`
  try {
    let did = localStorage.getItem('_track_device_id')
    if (!did) {
      did = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem('_track_device_id', did)
    }
    return did
  } catch {
    return `dev_${Date.now()}`
  }
}

/**
 * @description 获取埋点设备 ID（未登录用户 UV 去重回退标识）
 * @returns {string}
 */
export function getTrackDeviceId() {
  return _getDeviceId()
}

function _enqueue(payload) {
  const key = _getEventKey(
    payload.eventName,
    payload.targetType,
    payload.targetId,
    payload.eventType,
    payload.properties
  )
  const now = Date.now()
  const existing = _TRACK_DEDUP_MAP.get(key)

  if (existing && now - existing.lastTime < DEDUP_WINDOW_MS) {
    existing.count += 1
    existing.lastTime = now
    existing.item.properties = {
      ...existing.item.properties,
      ...(payload.properties || {}),
      _merged_count: existing.count,
    }
    return
  }

  const item = {
    ...payload,
    properties: { ...(payload.properties || {}), _merged_count: 1 },
    _dedupKey: key,
    ts: now,
  }

  _TRACK_DEDUP_MAP.set(key, { count: 1, lastTime: now, item })
  _TRACK_QUEUE.push(item)
  _checkFlush()
}

function _checkFlush() {
  if (_TRACK_QUEUE.length >= FLUSH_IMMEDIATE_SIZE) {
    _flush()
  }
  if (!_flushTimer) {
    _flushTimer = setTimeout(() => {
      _flushTimer = null
      _flush()
    }, FLUSH_INTERVAL)
  }
}

/**
 * @description 出站前剔除 product/item/scheme 等大对象，避免方案详情等把整页 CMS 塞进监控导致数值/目标字段丢失
 * @param {Object} item
 * @returns {Object}
 */
function toOutboundTrackPayload(item) {
  const {
    eventName,
    properties,
    eventType,
    pageName,
    product: _product,
    item: _item,
    scheme: _scheme,
    modelNo: _modelNo,
    ...topLevelFields
  } = item || {}
  return {
    eventName,
    eventType,
    pageName,
    ...topLevelFields,
    ...(properties && typeof properties === 'object' ? properties : {}),
  }
}

function _flush() {
  if (_isFlushing || _TRACK_QUEUE.length === 0) return
  _isFlushing = true

  const batch = _TRACK_QUEUE.splice(0, MAX_QUEUE_SIZE)
  batch.forEach((item) => {
    _TRACK_DEDUP_MAP.delete(item._dedupKey)
  })

  const fm = getFM()
  if (fm && typeof fm.track === 'function') {
    try {
      const trackItems = batch.map(({ _dedupKey, ts, ...rest }) => rest)
      if (typeof fm.trackBatch === 'function') {
        fm.trackBatch(trackItems.map((item) => toOutboundTrackPayload(item)))
      } else {
        trackItems.forEach((item) => {
          const outbound = toOutboundTrackPayload(item)
          const { eventName, eventType, pageName, ...data } = outbound
          fm.track(eventName, data, eventType, pageName)
        })
      }
    } catch (e) {
      console.warn('[Tracker] flush fail:', e.message)
      _TRACK_QUEUE.unshift(...batch)
      if (_TRACK_QUEUE.length > MAX_QUEUE_SIZE * 2) {
        _TRACK_QUEUE.splice(MAX_QUEUE_SIZE)
      }
    }
  }

  _isFlushing = false
}

export function flushTrackQueue() {
  _flush()
}

const PRODUCT_MODEL_TARGET_TYPES = new Set([
  'mall_homepage_prod',
  'mall_homepage_activity_product',
  'mall_add_to_cart',
  'mall_product',
  'product_detail',
  'product_detail_tab',
  'product_service_video',
  'product_service_video_tab',
  'activity_page_prod',
  'mall_list_prod',
  'homepage_card',
  'confirm_page',
  'mega_menu_product',
  'category_pk_product',
  'category_product_card',
  'cart_product',
])

/**
 * @description 判断事件是否需要补齐商品型号 / 品名等字段
 * @param {Object} payload
 * @returns {boolean}
 */
function isProductModelTrackingEvent(payload) {
  const eventName = payload.eventName || ''
  const targetType = payload.targetType || ''
  const properties = payload.properties || {}

  if (PRODUCT_MODEL_TARGET_TYPES.has(targetType)) return true
  if (targetType === 'mall_list' && (properties.prod_id || properties.prod_name)) return true
  if (properties.source === 'product_detail' && (properties.prod_id || properties.model_no)) {
    return true
  }
  if (
    /(?:Card|Banner|AddToCart|BuyNow|AddComparison|RemoveComparison|ProductDetailPage|ActivityCard|ProductCard|ConfirmPage|Cart_Delete|Collect_click|_prod)/.test(
      eventName
    )
  ) {
    return true
  }
  return false
}

/**
 * @description 补齐商品埋点的 model_no / prod_id / prod_name（兼容多字段别名）
 * @param {Object} payload
 * @returns {Object}
 */
function enrichProductTrackingPayload(payload) {
  if (!isProductModelTrackingEvent(payload)) return payload

  const props = { ...(payload.properties || {}) }
  const productSource = payload.product || payload.item || null
  const fields = productSource ? buildProductTrackFields(productSource) : null

  const modelNo =
    resolveProductModelNo(payload.modelNo) ||
    resolveProductModelNo(props) ||
    (fields?.modelNo || '') ||
    resolveProductModelNo(payload.item) ||
    resolveProductModelNo(payload.product)

  props.model_no = modelNo || props.model_no || ''

  const rawProdId =
    String(props.prod_id || '').trim() ||
    fields?.properties?.prod_id ||
    String(productSource?.prodId ?? productSource?.productId ?? '').trim()
  // 不用复合 id / 纯展示 id 冒充 prod_id（活动卡 id 常为 activityId-prodId）
  if (rawProdId) props.prod_id = rawProdId

  let prodName =
    String(props.prod_name || '').trim() ||
    fields?.targetName ||
    resolveProductName(productSource) ||
    resolveProductName(props) ||
    modelNo

  // 监控「商品名称」列在 prod_name 为空时会回退成 prod_id，禁止用 ID 填名称
  if (prodName && props.prod_id && prodName === String(props.prod_id)) {
    prodName = modelNo && modelNo !== prodName ? modelNo : ''
  }
  if (prodName) props.prod_name = prodName

  if (!props.entity_type && (props.prod_id || props.model_no || props.prod_name)) {
    props.entity_type = 'prod'
  }

  const targetName =
    String(payload.targetName || '').trim() ||
    prodName ||
    String(props.scheme_name || '').trim() ||
    String(props.case_name || '').trim() ||
    String(props.category_name || '').trim() ||
    String(props.file_name || '').trim() ||
    ''

  // 若业务侧误把 pageName（如「商品详情页」）写入 targetName，有商品名时覆盖回去
  const pageName = String(payload.pageName || '').trim()
  const resolvedTargetName =
    targetName && pageName && targetName === pageName && prodName ? prodName : targetName

  const { modelNo: _modelNo, item: _item, product: _product, ...rest } = payload
  return {
    ...rest,
    ...(resolvedTargetName ? { targetName: resolvedTargetName } : {}),
    properties: props,
  }
}

/**
 * @description 补齐活动维度字段 activity_id / activity_name / activity_type（对齐监控活动维度分析）
 * @param {Object} payload
 * @returns {Object}
 */
function enrichActivityTrackingPayload(payload) {
  const props = { ...(payload.properties || {}) }
  const productSource = payload.product || payload.item || null
  const fromProduct = resolveActivityTrackFields(productSource)
  const fromProps = resolveActivityTrackFields(props)

  const activityId =
    String(props.activity_id || '').trim() ||
    fromProduct.activityId ||
    fromProps.activityId ||
    (String(payload.sourceType || props.source_type || '') === 'activity_page'
      ? String(payload.sourceId || props.source_id || '').trim()
      : '')
  const activityName =
    String(props.activity_name || '').trim() ||
    fromProduct.activityName ||
    fromProps.activityName ||
    (String(payload.sourceType || props.source_type || '') === 'activity_page'
      ? String(payload.sourceName || props.source_name || '').trim()
      : '')
  const activityType =
    String(props.activity_type || '').trim() ||
    fromProduct.activityType ||
    fromProps.activityType

  if (!activityId && !activityName && !activityType) return payload

  if (activityId) props.activity_id = activityId
  if (activityName) props.activity_name = activityName
  if (activityType) props.activity_type = activityType

  return {
    ...payload,
    properties: props,
  }
}

/**
 * @description 合并来源字段到顶层 + properties（供监控提升为可筛 source_*）
 * @param {Object} payload
 * @returns {Object}
 */
function enrichSourceTrackingPayload(payload) {
  const props = { ...(payload.properties || {}) }
  const source = buildTrackSourceFields({
    sourceType: payload.sourceType || props.source_type || props.sourceType,
    sourceId: payload.sourceId || props.source_id || props.sourceId,
    sourceName: payload.sourceName || props.source_name || props.sourceName,
    fromPage: payload.fromPage || props.from_page || props.fromPage,
  })

  if (!source.sourceType && !source.sourceId && !source.sourceName && !source.fromPage) {
    return payload
  }

  return {
    ...payload,
    sourceType: source.sourceType || payload.sourceType || '',
    sourceId: source.sourceId || payload.sourceId || '',
    sourceName: source.sourceName || payload.sourceName || '',
    properties: {
      ...props,
      ...source.properties,
    },
  }
}

/**
 * @description 合并客户类型等用户身份字段（身份字段以当前登录态为准，避免业务侧漏传导致无法筛选）
 * @param {Object} payload
 * @returns {Object}
 */
function enrichUserTrackingPayload(payload) {
  const userCtx = getTrackUserContext()
  return {
    ...payload,
    properties: {
      ...(payload.properties || {}),
      ...userCtx,
    },
  }
}

/**
 * @description 补齐顶层 targetName（监控「目标名称」列依赖此字段）
 * @param {Object} payload
 * @returns {Object}
 */
function enrichTargetNamePayload(payload) {
  const existing = String(payload.targetName || '').trim()
  const pageName = String(payload.pageName || '').trim()
  const props = payload.properties || {}
  const entityName =
    String(props.prod_name || '').trim() ||
    String(props.model_no || '').trim() ||
    String(props.scheme_name || '').trim() ||
    String(props.case_name || '').trim() ||
    String(props.category_name || '').trim() ||
    String(props.file_name || '').trim() ||
    String(props.content_title || '').trim() ||
    String(props.banner_name || '').trim() ||
    String(props.tab_name || '').trim() ||
    String(props.video_title || '').trim() ||
    String(props.item_name || '').trim()

  // 已有真实目标名（且不是 pageName 占位）则保留
  if (existing && (!pageName || existing !== pageName)) return payload
  // 商品/方案/案例详情等实体事件：禁止用 pageName 充当目标名称
  const isEntityDetailEvent =
    /(?:ProductDetailPage|SchemeDetailPage|CaseDetailPage)/i.test(String(payload.eventName || '')) ||
    payload.targetType === 'product_detail' ||
    payload.targetType === 'scheme_detail' ||
    payload.targetType === 'case_detail'
  if (existing && existing === pageName && entityName) {
    return { ...payload, targetName: entityName }
  }
  if (!entityName) {
    if (isEntityDetailEvent) return payload
    if (!existing && pageName) return { ...payload, targetName: pageName }
    return payload
  }
  return { ...payload, targetName: entityName }
}

/**
 * @description 入队上报（含 enrich）
 * @param {Object} payload
 * @returns {Object}
 */
function dispatchTrack(payload) {
  const enrichedPayload = enrichUserTrackingPayload(
    enrichSourceTrackingPayload(
      enrichActivityTrackingPayload(
        enrichTargetNamePayload(
          enrichProductTrackingPayload({
            ...payload,
            sessionId: _getSessionId(),
            deviceId: _getDeviceId(),
          })
        )
      )
    )
  )
  _enqueue(enrichedPayload)
  return enrichedPayload
}

/**
 * @description 业务埋点上报（V2 规范；商品/方案通过事件名 ProductCard/ActivityCard/HotScheme 等区分）
 * @param {Object} payload
 * @returns {void}
 */
export function safeTrack(payload) {
  if (!import.meta.client) return

  try {
    if (!payload || typeof payload !== 'object') {
      console.warn('[Tracker] safeTrack payload must be an object')
      return
    }

    const { eventName, eventType, targetType, targetId } = payload
    if (!eventName || !eventType || !targetType || !targetId) {
      console.warn('[Tracker] safeTrack missing required fields:', {
        eventName,
        eventType,
        targetType,
        targetId,
      })
      return
    }

    if (!_fm) {
      if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.log('[Tracker:dev]', payload)
      }
      return
    }

    dispatchTrack(payload)
  } catch (e) {
    console.warn('[Tracker] track fail:', e.message)
  }
}

/**
 * @description 加购成功埋点
 * @param {Object} [payload]
 * @returns {void}
 */
export function trackAddToCartSuccess(payload = {}) {
  const {
    eventName = 'pv_MallList_AddToCart_click',
    pageName = '商城',
    targetId,
    targetName = '',
    modelNo = '',
    quantity = 1,
    skuId,
    fromPage = '',
    sourceType = '',
    sourceId = '',
    sourceName = '',
    properties = {},
    item,
    product,
  } = payload

  if (!targetId) return

  const resolvedModelNo =
    resolveProductModelNo(modelNo) ||
    resolveProductModelNo(properties) ||
    resolveProductModelNo(item) ||
    resolveProductModelNo(product)

  safeTrack({
    eventName: toPvEventName(eventName),
    eventType: 'interact',
    targetType: 'mall_add_to_cart',
    targetId: String(targetId),
    targetName,
    pageName,
    sourceType,
    sourceId,
    sourceName,
    modelNo: resolvedModelNo,
    product: product || item,
    item: item || product,
    properties: {
      quantity: Number(quantity) || 1,
      sku_id: skuId ? String(skuId) : '',
      from_page: fromPage,
      ...(sourceType ? { source_type: sourceType } : {}),
      ...(sourceId ? { source_id: sourceId } : {}),
      ...(sourceName ? { source_name: sourceName } : {}),
      ...properties,
    },
  })
}

export function usePageTimer() {
  let startTime = 0
  let isTiming = false

  return {
    /**
     * @description 开始计时
     * @param {{ force?: boolean }} [options] - force=true 时重置起点（PV 就绪 / 回前台）
     * @returns {void}
     */
    beginTiming(options = {}) {
      if (!isTiming || options.force) {
        startTime = Date.now()
        isTiming = true
      }
    },
    endTiming() {
      if (!isTiming) return 0
      isTiming = false
      return Date.now() - startTime
    },
    getElapsed() {
      return isTiming ? Date.now() - startTime : 0
    },
  }
}

export function useSegmentedTimer() {
  const segments = {}
  let currentSegment = null
  let currentStart = 0

  return {
    switchTo(segmentName) {
      const now = Date.now()
      if (currentSegment && segments[currentSegment] !== undefined) {
        segments[currentSegment] += now - currentStart
      }
      if (!segments[segmentName]) {
        segments[segmentName] = 0
      }
      currentSegment = segmentName
      currentStart = now
    },
    collect() {
      const now = Date.now()
      if (currentSegment && segments[currentSegment] !== undefined) {
        segments[currentSegment] += now - currentStart
        currentStart = now
      }
      return { ...segments }
    },
    reset() {
      for (const key in segments) {
        delete segments[key]
      }
      currentSegment = null
      currentStart = 0
    },
  }
}

function _calcVisibleRatio(rect, viewportWidth, viewportHeight) {
  const left = rect.left ?? 0
  const top = rect.top ?? 0
  const width = rect.width ?? Math.max(0, (rect.right ?? left) - left)
  const height = rect.height ?? Math.max(0, (rect.bottom ?? top) - top)
  if (width <= 0 || height <= 0) return 0

  const overlapW = Math.max(0, Math.min(left + width, viewportWidth) - Math.max(left, 0))
  const overlapH = Math.max(0, Math.min(top + height, viewportHeight) - Math.max(top, 0))
  return (overlapW * overlapH) / (width * height)
}

export function isSelectorInViewport(selector, options = {}) {
  const threshold = options.threshold ?? 0.5
  if (!import.meta.client) return Promise.resolve(false)

  try {
    const el = document.querySelector(selector)
    if (!el) return Promise.resolve(false)
    const rect = el.getBoundingClientRect()
    const ratio = _calcVisibleRatio(rect, window.innerWidth, window.innerHeight)
    return Promise.resolve(ratio >= threshold)
  } catch {
    return Promise.resolve(false)
  }
}

export function useViewportExposure(options = {}) {
  const { threshold = 0.5, rootMargin = '0px' } = options
  const ioObservers = []
  const trackedElements = new Map()
  const observerConfigs = []
  const registeredKeys = new Set()
  let pollTimer = null
  let pollTicking = false

  function _resolveTrackKey(rawKey, observerKey) {
    if (rawKey === undefined || rawKey === null || rawKey === '') return null
    return `${observerKey}:${rawKey}`
  }

  function _getBusinessKeyFromDataset(ds) {
    return getTrackBusinessKeyFromDataset(ds)
  }

  function _getRawKeyFromEl(el) {
    const fromSelf = _getBusinessKeyFromDataset(el?.dataset)
    if (fromSelf) return fromSelf
    if (el?.id) return el.id
    // 兼容「观察父容器、业务键写在子节点」：否则 intersection 回调因无 trackKey 被静默跳过
    const child = el?.querySelector?.(TRACK_BUSINESS_KEY_SELECTOR)
    if (child) {
      const fromChild = _getBusinessKeyFromDataset(child.dataset)
      if (fromChild) return fromChild
    }
    return null
  }

  function _markVisible(trackKey, onEnterViewport, el) {
    if (!trackKey || trackedElements.has(trackKey)) return
    trackedElements.set(trackKey, true)
    onEnterViewport(el, trackKey)
  }

  function _runRescan(targetObserverKey) {
    if (!import.meta.client) return

    const configs = targetObserverKey
      ? observerConfigs.filter((cfg) => cfg.observerKey === targetObserverKey)
      : observerConfigs
    if (!configs.length) return

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    configs.forEach(({ selector, onEnterViewport, observerKey }) => {
      document.querySelectorAll(selector).forEach((el) => {
        const trackKey = _resolveTrackKey(_getRawKeyFromEl(el), observerKey)
        if (!trackKey) return
        const rect = el.getBoundingClientRect()
        if (_calcVisibleRatio(rect, viewportWidth, viewportHeight) >= threshold) {
          _markVisible(trackKey, onEnterViewport, el)
        }
      })
    })
  }

  function rescan(observerKey) {
    // 动态列表补绑新节点到 IntersectionObserver，再做一次即时可视扫描
    ioObservers
      .filter((item) => !observerKey || item.observerKey === observerKey)
      .forEach(({ bindElements }) => bindElements?.())
    _runRescan(observerKey)
  }

  function _ensurePollTimer() {
    if (pollTimer) return
    pollTimer = setInterval(() => {
      if (pollTicking) return
      pollTicking = true
      _runRescan()
      pollTicking = false
    }, 500)
  }

  function observe(selector, onEnterViewport, observerKey = selector) {
    if (!import.meta.client) return

    if (registeredKeys.has(observerKey)) {
      rescan(observerKey)
      return
    }
    registeredKeys.add(observerKey)
    observerConfigs.push({ selector, onEnterViewport, observerKey })

    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const cfg = observerConfigs.find((c) => c.observerKey === observerKey)
            if (!cfg) return
            const rawKey = _getRawKeyFromEl(entry.target)
            const trackKey = _resolveTrackKey(rawKey, observerKey)
            if (entry.intersectionRatio >= threshold) {
              _markVisible(trackKey, cfg.onEnterViewport, entry.target)
            }
          })
        },
        { threshold, rootMargin }
      )

      const bindElements = () => {
        document.querySelectorAll(selector).forEach((el) => {
          observer.observe(el)
        })
      }
      bindElements()
      ioObservers.push({ observer, bindElements, observerKey })
    }
    _ensurePollTimer()
    rescan(observerKey)
  }

  function disconnect() {
    ioObservers.forEach(({ observer }) => observer.disconnect())

    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }

    ioObservers.length = 0
    observerConfigs.length = 0
    registeredKeys.clear()
    trackedElements.clear()
  }

  return { observe, rescan, disconnect }
}
